import React, { createContext, useContext, useState, useEffect, useCallback, useRef } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { apiRequest } from "@/lib/queryClient";
import { useAuth } from "./AuthContext";

export interface OnboardingStep {
  id: string;
  title: string;
  description: string;
  /** Primary URL to navigate to for this step */
  primaryUrl: string;
  /** All URLs that "count" for this step (used for sidebar highlighting) */
  highlightUrls: string[];
  section: string;
  actionLabel: string;
}

// ── Admin / Owner steps (8) ─────────────────────────────────────────────────
export const ADMIN_ONBOARDING_STEPS: OnboardingStep[] = [
  {
    id: "dashboard",
    title: "Explore Your Dashboard",
    description:
      "Your home base. See live job stats, revenue summaries, and team activity at a glance — everything you need to know at a single view.",
    primaryUrl: "/",
    highlightUrls: ["/"],
    section: "WORK",
    actionLabel: "Go to Dashboard",
  },
  {
    id: "form-submissions",
    title: "Set Up Lead Capture via Forms",
    description:
      "Form Submissions shows inbound leads from your website contact forms. Connect your forms to auto-create jobs and never miss a lead.",
    primaryUrl: "/form-submissions",
    highlightUrls: ["/form-submissions"],
    section: "WORK",
    actionLabel: "View Form Submissions",
  },
  {
    id: "integrations",
    title: "Connect Integrations & Automations",
    description:
      "Sync with Xero for accounting, connect Stripe for online payments, and activate automated SMS/email workflows to save hours every week.",
    primaryUrl: "/integrations",
    highlightUrls: ["/integrations", "/automation-settings"],
    section: "OPERATIONS",
    actionLabel: "Set Up Integrations",
  },
  {
    id: "team",
    title: "Add Your Team Members",
    description:
      "Invite staff to your workspace. Assign roles (Admin, Staff), set their work colors, and let them log into their own staff portal.",
    primaryUrl: "/team",
    highlightUrls: ["/team"],
    section: "STAFF",
    actionLabel: "Manage Team",
  },
  {
    id: "staff-ops",
    title: "Manage Staff Operations",
    description:
      "View staff availability to avoid scheduling conflicts, use Staff Schedule to plan jobs, and track hours with Timesheets.",
    primaryUrl: "/staff-availability",
    highlightUrls: ["/staff-availability", "/staff-schedule", "/timesheets"],
    section: "STAFF",
    actionLabel: "Staff Availability",
  },
  {
    id: "customers",
    title: "Build Your Customer Base",
    description:
      "Add customers, respond to enquiries in your Inbox, and keep all communication flowing via Messages — your CRM in one place.",
    primaryUrl: "/customers",
    highlightUrls: ["/customers", "/inbox", "/messages"],
    section: "CUSTOMERS",
    actionLabel: "View Customers",
  },
  {
    id: "jobs",
    title: "Create Jobs, Quotes & Invoices",
    description:
      "The core workflow: create Jobs, send Quotes for approval, schedule work on the Map & Scheduler, then convert to Invoices.",
    primaryUrl: "/jobs",
    highlightUrls: ["/jobs", "/quotes", "/scheduler", "/map", "/invoices"],
    section: "WORK",
    actionLabel: "View Jobs",
  },
  {
    id: "settings",
    title: "Configure Settings & Billing",
    description:
      "Set your business profile, bank details for invoices, GST settings, and activate your subscription to unlock all features.",
    primaryUrl: "/settings",
    highlightUrls: ["/settings", "/settings/business", "/billing-settings"],
    section: "SYSTEM",
    actionLabel: "Open Settings",
  },
];

// ── Staff steps (4) ─────────────────────────────────────────────────────────
export const STAFF_ONBOARDING_STEPS: OnboardingStep[] = [
  {
    id: "my-jobs",
    title: "View Your Assigned Jobs",
    description:
      "My Jobs is your personal task list. See every job assigned to you, check details, update status, and log time — all in one place.",
    primaryUrl: "/staff-dashboard",
    highlightUrls: ["/staff-dashboard"],
    section: "MY WORKSPACE",
    actionLabel: "Go to My Jobs",
  },
  {
    id: "my-availability",
    title: "Set Your Availability",
    description:
      "Let your team know when you're available. Mark leave days, update your schedule, and keep managers in the loop so jobs are scheduled around you.",
    primaryUrl: "/staff-availability",
    highlightUrls: ["/staff-availability"],
    section: "MY WORKSPACE",
    actionLabel: "Manage Availability",
  },
  {
    id: "map-view",
    title: "Check the Map View",
    description:
      "See all active jobs pinned on the map. Find job locations quickly, get directions, and see where your team is working today.",
    primaryUrl: "/map",
    highlightUrls: ["/map"],
    section: "MY WORKSPACE",
    actionLabel: "Open Map",
  },
  {
    id: "staff-settings",
    title: "Review Your Settings",
    description:
      "Update your name, contact info, and notification preferences. Keep your profile up to date so you never miss important job updates.",
    primaryUrl: "/staff-settings",
    highlightUrls: ["/staff-settings"],
    section: "MY WORKSPACE",
    actionLabel: "Open Settings",
  },
];

/** Pick the correct step list based on the user's role */
export function getStepsForRole(role: string | undefined): OnboardingStep[] {
  if (role === "staff") return STAFF_ONBOARDING_STEPS;
  return ADMIN_ONBOARDING_STEPS;
}

interface OnboardingTourContextValue {
  /** Is the tour currently visible (not dismissed) */
  isVisible: boolean;
  /** Was the tour previously dismissed (for "Setup Guide" reopen logic) */
  wasDismissed: boolean;
  /** Index of the currently active step (0-based) */
  currentStepIndex: number;
  /** The current step object */
  currentStep: OnboardingStep;
  /** The appropriate step list for the current user role */
  steps: OnboardingStep[];
  /** URLs to highlight in the sidebar right now */
  highlightedUrls: string[];
  /** Total step count */
  totalSteps: number;
  /** Navigate to prev step */
  prevStep: () => void;
  /** Navigate to next step */
  nextStep: () => void;
  /** Go to a specific step */
  goToStep: (index: number) => void;
  /** Permanently dismiss the tour */
  dismiss: () => void;
  /** Re-open the tour from "Setup Guide" menu item */
  reopen: () => void;
  /** Whether dismiss is in progress */
  isDismissing: boolean;
  /** Whether reopen is in progress */
  isReopening: boolean;
}

const OnboardingTourContext = createContext<OnboardingTourContextValue | null>(null);

export function OnboardingTourProvider({ children }: { children: React.ReactNode }) {
  const { user, isAdmin, isSuperAdmin, isLoading: authLoading } = useAuth();
  const queryClient = useQueryClient();
  const initialisedForUser = useRef<string | null>(null);

  const steps = getStepsForRole(user?.role);

  // ── localStorage cache for instant tour display ────────────────────────────
  const cacheKey = user?.id ? `tour_state_${user.id}` : null;

  const getCachedTourState = useCallback((): {
    tourDismissed: boolean;
    tourStep: number;
    onboardingComplete: boolean;
  } | null => {
    if (!cacheKey) return null;
    try {
      const raw = localStorage.getItem(cacheKey);
      if (raw) return JSON.parse(raw);
    } catch { /* ignore */ }
    return null;
  }, [cacheKey]);

  const setCachedTourState = useCallback(
    (data: { tourDismissed: boolean; tourStep: number; onboardingComplete: boolean }) => {
      if (!cacheKey) return;
      try {
        localStorage.setItem(cacheKey, JSON.stringify(data));
      } catch { /* ignore */ }
    },
    [cacheKey]
  );

  // Start at cached step or 0
  const cachedState = getCachedTourState();
  const [currentStepIndex, setCurrentStepIndex] = useState<number>(
    cachedState ? Math.max(0, Math.min(cachedState.tourStep ?? 0, steps.length - 1)) : 0
  );

  // Fetch profile to check tourDismissed + tourStep
  const { data: profile, isLoading: profileLoading } = useQuery<{
    tourDismissed: boolean;
    tourStep: number;
    onboardingComplete: boolean;
  }>({
    queryKey: ["/profile/me"],
    enabled: !!user && !authLoading,
    staleTime: 5 * 60 * 1000,
    // Use cached data as placeholder so we don't flash/wait
    placeholderData: cachedState ?? undefined,
  });

  // Persist to localStorage whenever API data arrives
  useEffect(() => {
    if (profile) {
      setCachedTourState({
        tourDismissed: profile.tourDismissed,
        tourStep: profile.tourStep,
        onboardingComplete: profile.onboardingComplete,
      });
    }
  }, [profile, setCachedTourState]);

  // Sync DB step → local state once per user
  useEffect(() => {
    if (!profile || !user) return;
    if (initialisedForUser.current === user.id) return;
    initialisedForUser.current = user.id;
    const idx = Math.max(0, Math.min(profile.tourStep ?? 0, steps.length - 1));
    setCurrentStepIndex(idx);
  }, [profile, user, steps.length]);

  // ── Mutations ──────────────────────────────────────────────────────────────
  const saveStepMutation = useMutation({
    mutationFn: (step: number) =>
      apiRequest("POST", "/profile/save-tour-step", { step }),
  });

  const dismissMutation = useMutation({
    mutationFn: () => apiRequest("POST", "/profile/dismiss-tour"),
    onMutate: () => {
      // Optimistically update cache so tour hides instantly
      if (cacheKey) {
        const prev = getCachedTourState();
        if (prev) setCachedTourState({ ...prev, tourDismissed: true });
      }
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/profile/me"] });
    },
  });

  const reopenMutation = useMutation({
    mutationFn: () => apiRequest("POST", "/profile/reopen-tour"),
    onMutate: () => {
      // Optimistically update cache so tour shows instantly
      if (cacheKey) {
        const prev = getCachedTourState();
        if (prev) setCachedTourState({ ...prev, tourDismissed: false, tourStep: 0 });
      }
    },
    onSuccess: () => {
      setCurrentStepIndex(0);
      initialisedForUser.current = null; // allow re-sync
      queryClient.invalidateQueries({ queryKey: ["/profile/me"] });
    },
  });

  // ── Step navigation helpers ────────────────────────────────────────────────
  const persist = useCallback(
    (idx: number) => {
      setCurrentStepIndex(idx);
      saveStepMutation.mutate(idx);
      // Also update localStorage cache for instant restore on reload
      if (cacheKey) {
        const prev = getCachedTourState();
        if (prev) setCachedTourState({ ...prev, tourStep: idx });
      }
    },
    [saveStepMutation, cacheKey, getCachedTourState, setCachedTourState]
  );

  const prevStep = useCallback(() => {
    persist(Math.max(0, currentStepIndex - 1));
  }, [currentStepIndex, persist]);

  const nextStep = useCallback(() => {
    persist(Math.min(steps.length - 1, currentStepIndex + 1));
  }, [currentStepIndex, steps.length, persist]);

  const goToStep = useCallback(
    (index: number) => {
      persist(Math.max(0, Math.min(steps.length - 1, index)));
    },
    [steps.length, persist]
  );

  const dismiss = useCallback(() => {
    dismissMutation.mutate();
  }, [dismissMutation]);

  const reopen = useCallback(() => {
    reopenMutation.mutate();
  }, [reopenMutation]);

  // ── Visibility logic ──────────────────────────────────────────────────────
  // Use profile data if available, otherwise fall back to cached localStorage data
  // This makes the tour appear instantly on page reload without waiting for API
  const tourData = profile ?? cachedState;
  const hasTourData = !!tourData;

  // Admin/Owner: show after onboarding complete, not dismissed, not super admin
  // Staff: show when not dismissed (staff don't go through onboarding wizard)
  // Key change: we don't wait for profileLoading — we use cached state instead
  const isEligibleAdmin =
    !authLoading && hasTourData && !!user && isAdmin && !isSuperAdmin;
  const isEligibleStaff =
    !authLoading && hasTourData && !!user && user.role === "staff";

  const isDismissed = tourData?.tourDismissed === true;
  const onboardingComplete = tourData?.onboardingComplete === true;

  const isVisible =
    (isEligibleAdmin && !isDismissed && onboardingComplete) ||
    (isEligibleStaff && !isDismissed);

  const currentStep = steps[currentStepIndex] ?? steps[0];

  const value: OnboardingTourContextValue = {
    isVisible,
    wasDismissed: isDismissed,
    currentStepIndex,
    currentStep,
    steps,
    highlightedUrls: isVisible ? currentStep.highlightUrls : [],
    totalSteps: steps.length,
    prevStep,
    nextStep,
    goToStep,
    dismiss,
    reopen,
    isDismissing: dismissMutation.isPending,
    isReopening: reopenMutation.isPending,
  };

  return (
    <OnboardingTourContext.Provider value={value}>
      {children}
    </OnboardingTourContext.Provider>
  );
}

export function useOnboardingTour(): OnboardingTourContextValue {
  const ctx = useContext(OnboardingTourContext);
  if (!ctx) {
    throw new Error("useOnboardingTour must be used within OnboardingTourProvider");
  }
  return ctx;
}
